/**
 * Reset Password Page
 * 
 * Handles password reset confirmation with token from URL
 * Similar structure to AcceptInvitation page
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PageContainer } from '../components/UI/PageContainer';
import { PageHeader } from '../components/UI/PageHeader';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Card } from '../components/UI/Card';
import { useToastStore } from '../stores/toast';
import { confirmPasswordReset } from '../api/auth';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { getBaseUrl } from '../utils/env';

export function ResetPassword() {
  // Get token from URL search params - handle both encoded and unencoded
  const getTokenFromUrl = () => {
    try {
      // First try standard URLSearchParams
      const params = new URLSearchParams(window.location.search);
      let token = params.get('token');
      
      // If not found, try decoding the entire search string (handles double-encoding)
      if (!token && window.location.search) {
        const decoded = decodeURIComponent(window.location.search);
        const decodedParams = new URLSearchParams(decoded);
        token = decodedParams.get('token');
      }
      
      // If still not found, try parsing from hash or path
      if (!token) {
        const hash = window.location.hash;
        if (hash.includes('token=')) {
          const hashParams = new URLSearchParams(hash.substring(1));
          token = hashParams.get('token');
        }
      }
      
      return token;
    } catch (error) {
      console.error('Error parsing token from URL:', error);
      return null;
    }
  };
  
  const [token] = useState(getTokenFromUrl());
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { addToast } = useToastStore();

  // Navigation helper
  const navigate = (path: string) => {
    const baseUrl = getBaseUrl();
    const fullPath = baseUrl === '/' ? path : baseUrl.replace(/\/$/, '') + path;
    window.history.pushState({}, '', fullPath);
    // Trigger a popstate event to update the app's routing
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Validate password
  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordErrors = newPassword ? validatePassword(newPassword) : [];
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  // Calculate password strength
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (newPassword.length >= 12) strength++;
    if (/[A-Z]/.test(newPassword)) strength++;
    if (/[a-z]/.test(newPassword)) strength++;
    if (/[0-9]/.test(newPassword)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) strength++;

    setPasswordStrength(strength);
  }, [newPassword]);

  // Password reset mutation
  const resetMutation = useMutation({
    mutationFn: confirmPasswordReset,
    onSuccess: (data) => {
      addToast({
        type: 'success',
        message: data.message || 'Password reset successful! Please log in with your new password.',
      });
      // Redirect to login after a short delay
      setTimeout(() => {
        const baseUrl = getBaseUrl();
        const loginPath = baseUrl === '/' ? '/login' : baseUrl.replace(/\/$/, '') + '/login';
        window.location.href = window.location.origin + loginPath;
      }, 2000);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to reset password. The token may be invalid or expired.';
      addToast({
        type: 'error',
        message: errorMessage,
      });
    },
  });

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!token) {
      addToast({ 
        type: 'error', 
        message: 'Reset token is missing. Please check your email for the correct reset link.' 
      });
      return;
    }

    if (passwordErrors.length > 0) {
      addToast({ 
        type: 'error', 
        message: 'Please fix password requirements before submitting.' 
      });
      return;
    }

    if (!passwordsMatch) {
      addToast({ 
        type: 'error', 
        message: 'Passwords do not match' 
      });
      return;
    }

    resetMutation.mutate({
      token: token,
      new_password: newPassword,
    });
  };

  // Show error if no token
  if (!token) {
    return (
      <PageContainer>
        <Card className="max-w-md mx-auto mt-8">
          <div className="text-center p-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Invalid Reset Link
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No reset token provided. Please check your email for the password reset link.
            </p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // Show success state after successful reset
  if (resetMutation.isSuccess) {
    return (
      <PageContainer>
        <Card className="max-w-md mx-auto mt-8">
          <div className="text-center p-8">
            <div className="inline-flex p-4 bg-green-100 dark:bg-green-900/20 rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Password Reset Successful
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your password has been reset successfully. Redirecting to login...
            </p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // Main reset form
  return (
    <PageContainer>
      <div className="max-w-md mx-auto mt-8">
        <PageHeader
          icon={<Lock />}
          title="Reset Your Password"
          subtitle="Enter your new password below"
        />

        <Card>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                className="w-full"
                leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                }
              />
              {newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded ${
                          level <= passwordStrength
                            ? passwordStrength <= 2
                              ? 'bg-red-500'
                              : passwordStrength <= 3
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                            : 'bg-gray-200 dark:bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Password strength: {passwordStrength < 3 ? 'Weak' : passwordStrength < 4 ? 'Medium' : 'Strong'}
                  </p>
                  <div className="space-y-1">
                    {[
                      { check: newPassword.length >= 12, text: 'At least 12 characters' },
                      { check: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
                      { check: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
                      { check: /[0-9]/.test(newPassword), text: 'One number' },
                      { check: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword), text: 'One special character' },
                    ].map((req, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        {req.check ? (
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border-2 border-gray-300 dark:border-slate-600" />
                        )}
                        <span className={req.check ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}>
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Password must be at least 12 characters with uppercase, lowercase, numbers, and special characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full"
                leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                }
              />
              {confirmPassword && (
                <div className="mt-2">
                  {passwordsMatch ? (
                    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      Passwords match
                    </div>
                  ) : (
                    <div className="text-xs text-red-500 dark:text-red-400">
                      Passwords do not match
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-semibold mb-1">Security Note</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                    <li>Reset tokens expire after 1 hour</li>
                    <li>After reset, you'll need to log in with your new password</li>
                    <li>All existing sessions will be invalidated for security</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/login')}
                disabled={resetMutation.isPending}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Button>
              <Button
                type="submit"
                disabled={
                  resetMutation.isPending ||
                  !newPassword ||
                  !confirmPassword ||
                  passwordErrors.length > 0 ||
                  !passwordsMatch
                }
                className="flex-1"
              >
                {resetMutation.isPending ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Remember your password?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Log in here
            </button>
          </p>
        </div>
      </div>
    </PageContainer>
  );
}
